import { MoreVertical, X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { IconButton, OverflowMenu, Typography } from '../ui';
import { fileSubtitle } from '../file-detail/FileDetailMeta';
import { FileActionButton } from '../file-detail/FileActionButton';
import { useDocViewerShell, type DocViewerShellProps } from './DocViewerModal';
import styles from './DocViewerPane.module.css';

/**
 * The document viewer docked BESIDE the chat, for a file opened from the conversation. The reader keeps
 * the thread next to the document, so they can ask for a change while reading it, and the document updates
 * in place as the agent rewrites it (`useDocViewerShell` → live refresh). Spec:
 * `docs/superpowers/specs/2026-10-09-chat-file-pane-design.md`.
 *
 * The same viewer as the modal — `useDocViewerShell` owns the actions, the editor and the filing dialogs —
 * in a pane's chrome: the file name over its subtitle, the desktop action cluster, and a ✕ that closes the
 * pane and gives the chat its width back. Only rendered on WIDE layouts (`useChatFilePane`), so there is no
 * phone presenter here; a narrow chat opens the modal.
 *
 * **No Discuss.** The modal's footer primary opens a chat about the file. Here the chat is already beside
 * it, so Discuss would only open a SECOND conversation next to the one the reader is in.
 */
export function DocViewerPane(props: DocViewerShellProps) {
  const { workspaceName } = props;
  const { t } = useTranslation();
  const agent = useAgentName();
  const { file, close, modalBi, utilityActions, manageActions, body, dialogs } = useDocViewerShell(props);
  const subtitle = fileSubtitle(file, t, { workspaceName, assistantName: agent.short });

  return (
    <section className={styles.pane} aria-labelledby="doc-viewer-pane-title">
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <Typography id="doc-viewer-pane-title" variant="h4" component="h2" noWrap>
            {file.name}
          </Typography>
          {subtitle && (
            <Typography variant="p3" color="secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </div>
        <div className={styles.actions}>
          {utilityActions.map((action) => <FileActionButton key={action.key} action={action} onBi={modalBi} />)}
          {manageActions.length > 0 && (
            <OverflowMenu
              groups={[manageActions]}
              ariaLabel={t('agentCreations.table.options')}
              triggerIcon={<MoreVertical size={16} aria-hidden />}
              onOpen={() => modalBi('more_actions')}
            />
          )}
          <IconButton aria-label={t('common.close')} variant="text" onClick={close}>
            <X size={20} aria-hidden />
          </IconButton>
        </div>
      </header>
      <div className={styles.content}>{body}</div>
      {dialogs}
    </section>
  );
}
